<template>
  <!-- Area detail panel (ticket 08 box 4, Khalid: Beside). Modelled on the Registers record panel
       (RegisterTable.vue .row-details-panel): a sibling of the table inside the findings card, covers nothing. -->
  <aside
    ref="root"
    class="detail-panel"
    :class="{ 'is-accepted': accepted, 'is-rejected': rejected }"
    role="region"
    aria-labelledby="detailPanelTitle"
    tabindex="-1"
    @keydown.esc.stop="$emit('close')">
    <header class="detail-panel__header">
      <div class="detail-panel__mark" aria-hidden="true"><span class="dp-badge">{{ finding.badgeIndex }}</span></div>
      <div class="detail-panel__heading">
        <span>{{ eyebrow }}</span>
        <h2 id="detailPanelTitle">
          <span class="detail-panel__id">{{ finding.friendlyId || emDash }}</span>
          <span v-if="accepted" class="dp-pill dp-pill--accepted"><v-icon :size="12" class="dp-pill__icon">mdi-check</v-icon> Accepted</span>
          <span v-else-if="rejected" class="dp-pill dp-pill--rejected"><v-icon :size="12" class="dp-pill__icon">mdi-close</v-icon> Rejected</span>
        </h2>
        <p :title="location">{{ location }}</p>
      </div>
      <div class="detail-panel__tools">
        <v-btn icon small class="tool-btn" title="Previous finding" aria-label="Previous finding" :disabled="index <= 0" @click="$emit('step', -1)">
          <v-icon :size="20">mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon small class="tool-btn" title="Next finding" aria-label="Next finding" :disabled="index >= count - 1" @click="$emit('step', 1)">
          <v-icon :size="20">mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn icon small class="tool-btn" title="Close finding details" aria-label="Close finding details" @click="$emit('close')">
          <v-icon :size="20">mdi-close</v-icon>
        </v-btn>
      </div>
    </header>

    <div class="detail-panel__body">
      <!-- Component link (approved showcase ai-analysis-tag-picker-showcase.html): the inspector's own field, first. -->
      <section class="detail-panel__section detail-panel__section--component">
        <div class="detail-panel__section-heading"><div><span>Inspector</span><h3>Component</h3></div></div>
        <div class="comp-field-row">
          <button
            type="button"
            class="comp-field"
            :class="{ 'comp-field--none': !component }"
            aria-haspopup="dialog"
            :aria-label="component ? `Component: ${component.name}. Change` : 'Link to a component'"
            :disabled="busy"
            @click="$emit('link', finding)">
            <v-icon :size="18" class="comp-field__icon" :class="typeClass">{{ component ? componentIcon : 'mdi-link-variant-plus' }}</v-icon>
            <span class="comp-field__name">{{ component ? component.name : notLinkedFieldLabel }}</span>
            <span v-if="component && component.type" class="comp-field__type">{{ component.type }}</span>
            <v-icon :size="18" class="comp-field__caret" aria-hidden="true">mdi-menu-down</v-icon>
          </button>
          <v-btn v-if="component" icon small class="comp-field__clear" title="Remove link" aria-label="Remove the link" :disabled="busy" @click="$emit('unlink', finding)">
            <v-icon :size="18">mdi-link-variant-off</v-icon>
          </v-btn>
        </div>
        <div v-if="suggestedTag && !component" class="comp-suggest">
          <v-icon :size="16" class="comp-suggest__icon">mdi-lightbulb-outline</v-icon>
          <span>Suggested {{ suggestedFrom }}: <span class="comp-suggest__name">{{ suggestedTag.name }}</span></span>
          <v-btn x-small outlined color="primary" class="comp-suggest__use" :disabled="busy" @click="$emit('link-suggested', finding)">Use</v-btn>
        </div>
        <p class="comp-hint">{{ componentHint }}</p>
      </section>

      <section class="detail-panel__section">
        <div class="detail-panel__section-heading">
          <div><span>Tile</span><h3>Finding on the scan</h3></div>
          <v-btn small outlined color="primary" class="link-btn" @click="$emit('show-on-scan', finding)">
            <v-icon :size="14" left>mdi-crosshairs-gps</v-icon>
            {{ showOnScanLabel }}
          </v-btn>
        </div>
        <div v-if="tileUrl && !tileFailed" class="thumb" :style="{ aspectRatio: aspect }">
          <img :src="tileUrl" :alt="`Tile of finding ${finding.badgeIndex}`" @error="tileFailed = true" />
          <span class="thumb-tag"><v-icon :size="14" class="thumb-tag__icon">mdi-image-outline</v-icon>Tile · {{ scanName || emDash }}</span>
        </div>
        <div v-else class="thumb thumb--missing">
          <v-icon :size="18" class="thumb--missing__icon">mdi-image-off-outline</v-icon>
          <span>{{ tileUrl ? 'The tile could not be loaded' : 'No tile for this finding yet' }}</span>
        </div>
      </section>

      <section class="detail-panel__section">
        <div class="detail-panel__section-heading">
          <div><span>AI proposal</span><h3>Fields and values</h3></div>
          <strong>{{ proposalFieldCount }}</strong>
        </div>
        <div class="chip-row">
          <span v-if="!decided" class="dp-chip dp-chip--proposed">AI proposed</span>
          <span class="dp-chip dp-chip--severity" :style="{ background: severityStroke }">{{ severityLabel }}</span>
          <span class="dp-chip dp-chip--outline">Ri {{ ri }}&nbsp;<small>provisional, per patch</small></span>
          <span class="dp-chip dp-chip--outline">Cat {{ cat }}</span>
          <span class="dp-chip" :class="scabClass">Scab {{ finding.scabMeasure || emDash }}</span>
        </div>
        <dl class="fields">
          <dt>Type</dt><dd>{{ finding.type || emDash }}</dd>
          <dt>Visual description</dt><dd>{{ finding.description || emDash }}</dd>
          <dt>Scab reason</dt><dd>{{ finding.scabReason || emDash }}</dd>
          <dt>Recommended action</dt><dd>{{ finding.recommendedAction || emDash }}</dd>
          <dt>Relative visible area</dt><dd class="mono">{{ areaPercent }} <span class="muted">of the image</span></dd>
          <dt>Model</dt><dd class="mono">{{ finding.aiModel || notRecorded }}</dd>
          <dt>Detected</dt><dd class="mono">{{ detectedLabel }}</dd>
          <dt>Scan</dt><dd>{{ scanName || emDash }}</dd>
        </dl>
      </section>

      <section class="detail-panel__section">
        <div class="detail-panel__section-heading">
          <div><span>Confidence</span><h3>Five measures</h3></div>
          <strong>{{ confidences.length }}</strong>
        </div>
        <div class="conf">
          <div v-for="row in confidences" :key="row.label" class="conf-row" :class="`is-${row.band}`">
            <span class="label">{{ row.label }}</span>
            <span class="value">{{ row.text }}</span>
            <span class="bar"><i :style="{ width: row.text }"></i></span>
          </div>
        </div>
      </section>

      <section v-if="decided" class="detail-panel__section">
        <div class="detail-panel__section-heading"><div><span>Review</span><h3>Inspector decision</h3></div></div>
        <div class="decided-note">
          <v-icon :size="16" class="decided-note__icon">{{ accepted ? 'mdi-check-decagram' : 'mdi-cancel' }}</v-icon>
          <span>{{ decidedText }}</span>
        </div>
      </section>
    </div>

    <!-- Registers footer order: secondary on the left, the decisions right-aligned -->
    <footer class="detail-panel__footer">
      <v-btn small outlined class="foot-btn" :title="findingReportLabel" @click="$emit('report', finding)">
        <v-icon :size="16" left>mdi-file-document-outline</v-icon>
        Finding report
      </v-btn>
      <span class="spacer"></span>
      <v-btn small text color="error" class="foot-btn" :disabled="busy || rejected" @click="$emit('reject', finding)">
        <v-icon :size="16" left>mdi-close</v-icon>
        Reject
      </v-btn>
      <v-btn small depressed color="primary" class="foot-btn" :disabled="busy || decided" @click="$emit('approve', finding)">
        <v-icon :size="16" left>mdi-check</v-icon>
        Approve
      </v-btn>
    </footer>
  </aside>
</template>

<script>
  import { $formatDate } from '@/utils';
  import { APPROVAL, SCAB, parseMaskPoints, relativeArea, stripRi, stripCat, severityColor } from '../../utils/findingsMetrics';
  import { EM_DASH, formatPercent, ringBand } from '../../utils/reportFormat';
  import { userDisplayName } from '../../utils/jobsFormat';
  import { componentOfFinding, componentTypeIcon } from '../../utils/findingComponents';
  import { AREA_DECIMALS, DATE_FORMAT, SHOW_ON_SCAN_LABEL, NOT_RECORDED, NOT_LINKED_FIELD_LABEL, COMPONENT_HINT, SUGGESTED_FROM } from './reportConstants';

  const EYEBROW = 'AI detected finding';
  const DEFAULT_IMAGE_WIDTH = 4057;
  const DEFAULT_IMAGE_HEIGHT = 2028;
  const SEVERITY_LEVEL_OFFSET = 1; // the API stores the class index 0..3, AI Inspect shows Level 1..4
  const PROPOSAL_FIELD_COUNT = 12; // 4 chips + 8 fields
  const FINDING_REPORT_LABEL = 'Finding report (PDF)';
  const SCAB_CLASS = Object.freeze({
    [SCAB.YES]: 'dp-chip--scab-yes',
    [SCAB.POSSIBLE]: 'dp-chip--scab-possible',
    [SCAB.NO]: 'dp-chip--scab-no',
  });
  const CONFIDENCE_ROWS = Object.freeze([
    ['Detection', 'detectionConfidence'],
    ['Polygon', 'polygonConfidence'],
    ['Coating breakdown (Ri)', 'coatingBreakdownConfidence'],
    ['Corrosion category', 'pittingCategoryConfidence'],
    ['Scab measure', 'scabConfidence'],
  ]);
  const DECIDED_TRAIL = Object.freeze({
    [APPROVAL.ACCEPTED]: 'Added to AI Inspect.',
    [APPROVAL.REJECTED]: 'Removed from the report. This cannot be undone.',
  });

  export default {
    name: 'DetectionDetailPanel',
    props: {
      finding: { type: Object, required: true },
      /** { width, height } of the finding's image; missing sizes fall back to the equirectangular default. */
      imageSize: { type: Object, default: () => ({}) },
      scanName: { type: String, default: '' },
      /** Position of the finding in the report (0-based) and the report's area count, for ‹ ›. */
      index: { type: Number, default: 0 },
      count: { type: Number, default: 0 },
      busy: { type: Boolean, default: false },
      /** The AVA Inspect's component ({ id, name, type }) offered as a suggestion while the finding has none. */
      suggestedTag: { type: Object, default: null },
    },
    data() {
      return {
        tileFailed: false,
        emDash: EM_DASH,
        notRecorded: NOT_RECORDED,
        eyebrow: EYEBROW,
        showOnScanLabel: SHOW_ON_SCAN_LABEL,
        proposalFieldCount: PROPOSAL_FIELD_COUNT,
        findingReportLabel: FINDING_REPORT_LABEL,
        notLinkedFieldLabel: NOT_LINKED_FIELD_LABEL,
        componentHint: COMPONENT_HINT,
        suggestedFrom: SUGGESTED_FROM,
      };
    },
    computed: {
      accepted() {
        return this.finding.approvalStatus === APPROVAL.ACCEPTED;
      },
      rejected() {
        return this.finding.approvalStatus === APPROVAL.REJECTED;
      },
      decided() {
        return this.accepted || this.rejected;
      },
      location() {
        return this.finding.locationDescription || EM_DASH;
      },
      component() {
        return componentOfFinding(this.finding);
      },
      componentIcon() {
        return componentTypeIcon(this.component && this.component.type);
      },
      typeClass() {
        return this.component && this.component.type ? `t-${this.component.type}` : '';
      },
      tileUrl() {
        return this.finding.tiledImageUriPath || null;
      },
      width() {
        return Number(this.imageSize.width) || DEFAULT_IMAGE_WIDTH;
      },
      height() {
        return Number(this.imageSize.height) || DEFAULT_IMAGE_HEIGHT;
      },
      aspect() {
        return `${this.width} / ${this.height}`;
      },
      // maskPoints stays: the relative-area figure is derived from it.
      maskPoints() {
        return parseMaskPoints(this.finding.maskPoints);
      },
      /** The severity chip stays severity-coloured — it is labelled with the class it shows. */
      severityStroke() {
        return severityColor(this.finding.severity);
      },
      severityLabel() {
        const severity = this.finding.severity;
        if (severity === null || severity === undefined || severity === '') return `Severity ${EM_DASH}`;
        return `Severity class ${severity} · Level ${Number(severity) + SEVERITY_LEVEL_OFFSET}`;
      },
      ri() {
        return stripRi(this.finding.coatingBreakdown) || EM_DASH;
      },
      cat() {
        return stripCat(this.finding.pittingCategory) || EM_DASH;
      },
      scabClass() {
        return SCAB_CLASS[String(this.finding.scabMeasure || '').toUpperCase()] || 'dp-chip--scab-no';
      },
      areaPercent() {
        return formatPercent(relativeArea(this.maskPoints, this.width, this.height), AREA_DECIMALS);
      },
      detectedLabel() {
        return this.finding.createDate ? $formatDate(this.finding.createDate, DATE_FORMAT) : EM_DASH;
      },
      confidences() {
        return CONFIDENCE_ROWS.map(([label, key]) => {
          const value = Number(this.finding[key]) || 0;
          return { label, band: ringBand(value), text: formatPercent(value) };
        });
      },
      decidedText() {
        const status = this.finding.approvalStatus;
        const when = this.finding.approvalStatusChangedOn ? $formatDate(this.finding.approvalStatusChangedOn, DATE_FORMAT) : EM_DASH;
        return `${status} on ${when} by ${userDisplayName(this.finding.updatedBy)}. ${DECIDED_TRAIL[status] || ''}`.trim();
      },
    },
    watch: {
      'finding.id'() {
        this.tileFailed = false;
      },
    },
    mounted() {
      // Registers behaviour: the opened panel takes focus so Esc closes it and Tab stays inside.
      this.$nextTick(() => {
        if (this.$refs.root) this.$refs.root.focus();
      });
    },
  };
</script>

<style lang="scss">
  @import './aiAnalysisTokens';

  // Unscoped on purpose (Vuetify buttons and icons inside); every selector is rooted at .detail-panel.
  // Chip / pill / badge classes carry a dp- prefix so the findings table's unscoped rules never collide.
  .detail-panel {
    @include ai-analysis-tokens;
    --panel-width: 420px; // RegisterTable.vue .row-details-panel is 392px; Khalid asked for a little more (2026-09-06)

    display: flex;
    flex: 0 0 var(--panel-width);
    flex-direction: column;
    width: var(--panel-width);
    min-width: var(--panel-width);
    max-width: var(--panel-width);
    max-height: min(72vh, 820px); // same cap as the findings table wrap
    margin: var(--ds-space-sm) var(--ds-space-sm) var(--ds-space-sm) 0;
    border: 1px solid var(--aa-border-default);
    border-radius: var(--ds-radius-md);
    background: var(--aa-surface);
    color: var(--aa-text-primary);
    box-shadow: var(--ds-shadow-2);
    overflow: hidden;

    &:focus-visible { outline: 2px solid var(--aa-focus); outline-offset: -2px; }

    &__header {
      display: grid;
      grid-template-columns: 40px minmax(0, 1fr) auto;
      align-items: start;
      gap: var(--ds-space-sm);
      min-height: 88px;
      flex: 0 0 auto;
      padding: var(--ds-space-md);
      border-bottom: 1px solid var(--aa-border-subtle);
      background: var(--aa-surface-subtle);
    }
    &__mark { display: flex; width: 40px; height: 40px; align-items: center; justify-content: center; border-radius: var(--ds-radius-md); }
    &__heading {
      min-width: 0;
      > span { display: block; margin-bottom: 2px; color: var(--aa-brand); font-size: var(--aa-font-micro); font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
      h2 { display: flex; align-items: center; gap: var(--ds-space-sm); margin: 0; overflow: hidden; font-family: var(--ds-font-family-heading) !important; font-size: var(--ds-font-subtitle); font-weight: 600; line-height: 1.35; white-space: nowrap; }
      p { margin: 2px 0 0; overflow: hidden; color: var(--aa-text-muted); font-size: var(--ds-font-caption); line-height: 1.4; text-overflow: ellipsis; white-space: nowrap; }
    }
    &__id { font-family: var(--ds-font-family-mono) !important; font-size: var(--ds-font-body-sm); letter-spacing: 0.02em; overflow: hidden; text-overflow: ellipsis; }
    &__tools { display: flex; align-items: center; gap: 2px; margin: -4px -4px 0 0; }
    .tool-btn { color: var(--aa-text-secondary) !important; }

    &__body { min-height: 0; flex: 1 1 auto; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; }
    &__section { padding: var(--ds-space-md); }
    &__section + &__section { border-top: 1px solid var(--aa-border-subtle); }
    &__section-heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--ds-space-md);
      margin-bottom: var(--ds-space-sm);

      > div { min-width: 0; }
      span { display: block; color: var(--aa-text-muted); font-size: var(--aa-font-micro); font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
      h3 { margin: 2px 0 0; font-family: var(--ds-font-family-heading) !important; font-size: var(--ds-font-body); font-weight: 600; line-height: 1.35; }
      strong { flex: 0 0 auto; min-width: 28px; padding: 2px 8px; border-radius: var(--ds-radius-full); background: var(--aa-surface-hover); color: var(--aa-text-secondary); font-family: var(--ds-font-family-mono) !important; font-size: var(--ds-font-caption); font-weight: 600; text-align: center; font-variant-numeric: tabular-nums; }
      .link-btn { text-transform: none; letter-spacing: 0; font-size: var(--ds-font-caption); }
    }

    .dp-badge { display: inline-grid; place-items: center; width: 30px; height: 28px; border-radius: var(--ds-radius-sm); background: var(--aa-badge); color: #fff; font-family: var(--ds-font-family-mono) !important; font-size: var(--ds-font-caption); font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
    &.is-accepted .dp-badge { box-shadow: 0 0 0 2px var(--ds-brand-emerald); }
    &.is-rejected .dp-badge { background: var(--aa-text-disabled); }

    .dp-pill { display: inline-flex; align-items: center; gap: 4px; height: 20px; padding: 0 7px; border-radius: 2px; font-family: var(--ds-font-family-body); font-size: var(--aa-font-micro); font-weight: 600; letter-spacing: 0.03em; }
    .dp-pill__icon { color: inherit !important; }
    .dp-pill--accepted { background: rgba(3, 98, 76, 0.12); color: var(--aa-review-approved); }
    .dp-pill--rejected { background: rgba(183, 28, 28, 0.1); color: var(--aa-error); }

    .thumb { position: relative; border-radius: var(--ds-radius-sm); background: linear-gradient(160deg, #5b7c8d 0%, #2f3433 100%); overflow: hidden; }
    .thumb img { display: block; width: 100%; height: 100%; object-fit: cover; }
    .thumb-tag { position: absolute; left: var(--ds-space-sm); top: var(--ds-space-sm); display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: var(--ds-radius-sm); background: rgba(2, 26, 26, 0.72); color: #fff; font-size: var(--ds-font-caption); letter-spacing: 0.04em; }
    .thumb-tag__icon { color: inherit !important; }
    .thumb--missing { display: flex; align-items: center; justify-content: center; gap: 6px; aspect-ratio: 2 / 1; color: #cbd5e1; font-size: var(--ds-font-body-sm); background: linear-gradient(160deg, #12252b 0%, #0b1418 100%); }
    .thumb--missing__icon { color: inherit !important; }

    /* Component field (approved picker showcase): a button that reads as a select, a dashed one while unlinked. */
    --type-equipment: #215f9b;
    --type-pipeline: var(--aa-brand);
    --type-structural: #6d4c9b;
    .comp-field-row { display: flex; align-items: center; gap: 6px; }
    .comp-field {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      gap: 8px;
      min-width: 0;
      min-height: 40px;
      padding: 6px 8px 6px 10px;
      border: 1px solid var(--aa-border-strong);
      border-radius: var(--ds-radius-sm);
      background: var(--aa-surface);
      color: var(--aa-text-primary);
      font: inherit;
      text-align: left;
      cursor: pointer;

      &:hover { border-color: var(--aa-brand); }
      &:focus-visible { outline: none; border-color: var(--aa-brand); box-shadow: 0 0 0 2px var(--aa-brand-soft); }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
      &__icon { flex: 0 0 auto; color: var(--aa-text-secondary) !important; }
      &__name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: var(--ds-font-family-mono) !important; font-size: var(--ds-font-body-sm); }
      &__type { flex: 0 0 auto; color: var(--aa-text-muted); font-size: var(--ds-font-caption); }
      &__caret { flex: 0 0 auto; color: var(--aa-text-muted) !important; }
      &__clear { flex: 0 0 auto; color: var(--aa-text-muted) !important; }
      &__clear:hover { color: var(--aa-error) !important; }
    }
    .comp-field--none { border-style: dashed; color: var(--aa-text-muted); }
    .comp-field--none .comp-field__name { font-family: var(--ds-font-family-body) !important; }
    .comp-field--none .comp-field__icon { color: var(--aa-brand) !important; }
    .comp-field .t-Equipment { color: var(--type-equipment) !important; }
    .comp-field .t-Pipeline { color: var(--type-pipeline) !important; }
    .comp-field .t-Structural { color: var(--type-structural) !important; }
    .comp-suggest { display: flex; align-items: center; gap: 8px; margin-top: 8px; padding: 8px 10px; border-radius: var(--ds-radius-sm); background: var(--aa-brand-soft); color: var(--aa-text-secondary); font-size: var(--ds-font-caption); }
    .comp-suggest__icon { color: var(--aa-brand) !important; }
    .comp-suggest__name { font-family: var(--ds-font-family-mono) !important; color: var(--aa-text-primary); }
    .comp-suggest__use { margin-left: auto; text-transform: none; letter-spacing: 0.02em; }
    .comp-hint { margin: 6px 0 0; color: var(--aa-text-muted); font-size: var(--ds-font-caption); line-height: 1.5; }

    .chip-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
    .dp-chip { display: inline-flex; align-items: center; gap: 5px; height: 22px; padding: 0 8px; border-radius: var(--ds-radius-sm); border: 1px solid transparent; font-size: var(--ds-font-caption); font-weight: 600; letter-spacing: 0.04em; white-space: nowrap; }
    .dp-chip small { font-weight: 400; color: var(--aa-text-muted); }
    .dp-chip--proposed { text-transform: uppercase; color: var(--aa-brand); border-color: var(--aa-brand); }
    .dp-chip--severity { color: #fff; }
    .dp-chip--outline { border-color: var(--aa-border-strong); color: var(--aa-text-primary); }
    .dp-chip--scab-no { background: rgba(3, 98, 76, 0.1); color: var(--aa-scab-no); border-color: rgba(3, 98, 76, 0.3); }
    .dp-chip--scab-possible { background: rgba(180, 83, 9, 0.12); color: var(--aa-scab-possible); border-color: rgba(180, 83, 9, 0.32); }
    .dp-chip--scab-yes { background: rgba(183, 28, 28, 0.1); color: var(--aa-scab-yes); border-color: rgba(183, 28, 28, 0.3); }

    .fields { margin: 0; display: grid; grid-template-columns: max-content 1fr; gap: 6px var(--ds-space-md); font-size: var(--ds-font-body-sm); }
    .fields dt { font-weight: 600; color: var(--aa-text-secondary); white-space: nowrap; }
    .fields dd { margin: 0; color: var(--aa-text-primary); line-height: 1.45; overflow-wrap: anywhere; }
    .fields dd.mono { font-family: var(--ds-font-family-mono) !important; font-size: var(--ds-font-caption); font-variant-numeric: tabular-nums; }
    .fields .muted { color: var(--aa-text-muted); }

    .conf { display: grid; gap: 8px; }
    .conf-row { display: grid; grid-template-columns: 1fr auto; gap: 4px 10px; align-items: center; font-size: var(--ds-font-caption); }
    .conf-row .label { color: var(--aa-text-secondary); }
    .conf-row .value { font-family: var(--ds-font-family-mono) !important; font-weight: 600; font-variant-numeric: tabular-nums; }
    .conf-row .bar { grid-column: 1 / -1; height: 6px; border-radius: var(--ds-radius-full); background: var(--aa-border-default); overflow: hidden; }
    .conf-row .bar > i { display: block; height: 100%; border-radius: inherit; }
    .conf-row.is-high .value { color: var(--aa-brand); }
    .conf-row.is-high .bar > i { background: var(--aa-brand); }
    .conf-row.is-mid .value { color: var(--aa-ring-mid-text); }
    .conf-row.is-mid .bar > i { background: var(--aa-ring-mid); }
    .conf-row.is-low .value { color: var(--aa-error); }
    .conf-row.is-low .bar > i { background: var(--aa-error); }

    .decided-note { display: flex; align-items: flex-start; gap: 8px; font-size: var(--ds-font-caption); color: var(--aa-text-secondary); line-height: 1.5; }
    .decided-note__icon { color: var(--aa-brand) !important; }
    &.is-rejected .decided-note__icon { color: var(--aa-error) !important; }
    &.is-rejected .thumb, &.is-rejected .fields, &.is-rejected .conf, &.is-rejected .chip-row { opacity: 0.6; }

    &__footer {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: flex-end;
      gap: var(--ds-space-sm);
      padding: 10px 12px;
      border-top: 1px solid var(--aa-border-subtle);
      background: var(--aa-surface-subtle);

      .spacer { flex: 1 1 auto; }
      // Tight paddings so the three actions share one row inside the panel (approved showcase footer).
      .v-btn.foot-btn { padding: 0 8px; font-size: var(--ds-font-caption); letter-spacing: 0.03em; }
      .v-btn.foot-btn .v-icon--left { margin-right: 6px; }
    }
  }

  .theme--dark .detail-panel {
    @include ai-analysis-tokens-dark;
    --type-equipment: #7fb3e6;
    --type-structural: #b79cdc;
  }
</style>
